import {
  getExternalLink,
  isExternal,
  isRegistrationClosedVisible,
  isRegistrationScheduled,
  isRegistrationWithCustomDates,
  isTicketed,
  isWithoutRegistration,
  saleScheduledAndHasTickets,
  ticketedOrInitiallyTicketed,
} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React, {PropsWithChildren} from 'react'
import {useEventDateInformation} from '../../../../../../commons/hooks/dates'
import {useSettings} from '../../../../../../commons/hooks/use-settings'
import {getEventListPageUrl} from '../../../../../../commons/selectors/environment'
import settingsParams from '../../../../settingsParams'
import {useAppSelector} from '../../../hooks/runtime'
import {useVisibilityStyles} from '../../../hooks/use-visibility-styles'
import {getEvent} from '../../../selectors/event'
import {isMemberRsvpExists} from '../../../selectors/member-rsvp'
import {getTickets} from '../../../selectors/tickets'
import {ClosedRegistrationButton} from '../../registration-buttons/closed-registration-button'
import {EditRegistrationButton} from '../../registration-buttons/edit-registration-button'
import {RegistrationButtonSkin} from '../../registration-buttons/registration-button-skin'
import {TicketsUnavailableButton} from '../../registration-buttons/tickets-unavailable-button'
import {CustomDatesLabel} from '../custom-dates-label'
import {ButtonContainerProps, RegistrationButtonProps} from './interfaces'
import s from './registration-button.scss'

export const RegistrationButton = ({className, disableSpacing, narrowerButton, onClick}: RegistrationButtonProps) => {
  const {t} = useTranslation()
  const settings = useSettings()
  const {
    allBreakpoints: {isHeaderRegistrationButtonVisible},
  } = useVisibilityStyles()

  const event = useAppSelector(getEvent)
  const tickets = useAppSelector(getTickets)
  const memberRsvpExists = useAppSelector(isMemberRsvpExists)
  const eventListPageUrl = useAppSelector(getEventListPageUrl)

  const {formattedRegistrationStartDate: startDate, formattedRegistrationEndDate: endDate} = useEventDateInformation(
    event.id,
  )
  const ticketedOrInitiallyTicketedEvent = ticketedOrInitiallyTicketed(event)
  const ticketsUnavailable = isTicketed(event) && (!tickets.length || tickets.every(saleScheduledAndHasTickets))
  const rsvpButtonText = settings.getWithFallback(
    ticketedOrInitiallyTicketedEvent ? settingsParams.ticketedEventButtonText : settingsParams.rsvpEventButtonText,
    settingsParams.rsvpButtonText,
  )

  if (!isHeaderRegistrationButtonVisible()) {
    return null
  }

  const getButton = () => {
    if (isRegistrationScheduled(event)) {
      return (
        <>
          <ClosedRegistrationButton href={eventListPageUrl} />
          <CustomDatesLabel dataHook={DH.RSVP_OPENS_LABEL} text={t('registration.opensLabel', {date: startDate})} />
        </>
      )
    }

    if (ticketsUnavailable) {
      return <TicketsUnavailableButton href={eventListPageUrl} />
    }

    if (isRegistrationClosedVisible(event)) {
      return <ClosedRegistrationButton href={eventListPageUrl} />
    }

    if (memberRsvpExists) {
      return <EditRegistrationButton />
    }

    if (isWithoutRegistration(event)) {
      return null
    }

    return (
      <>
        <RegistrationButtonSkin
          dataHook={ticketedOrInitiallyTicketedEvent ? DH.HEADER_GET_TICKETS_BUTTON : DH.RSVP_BUTTON}
          text={rsvpButtonText}
          url={getExternalLink(event)}
          isExternalLink={isExternal(event)}
          narrowerButton={narrowerButton}
          onClick={onClick}
        />
        {isRegistrationWithCustomDates(event) && (
          <CustomDatesLabel dataHook={DH.RSVP_CLOSES_LABEL} text={t('registration.closesLabel', {date: endDate})} />
        )}
      </>
    )
  }

  const button = getButton()

  return button ? (
    <ButtonContainer className={classNames(s.container, className)} disableSpacing={disableSpacing}>
      {button}
    </ButtonContainer>
  ) : null
}

const ButtonContainer = ({className, disableSpacing, children}: PropsWithChildren<ButtonContainerProps>) => (
  <div className={classNames({[s.registrationButtonSpacing]: !disableSpacing}, className)}>{children}</div>
)
